/**
 * Passo de paciente do Pronto Atendimento.
 * Org → Cadastrar (GetPatient/MPI automático → Serpro → PostPatient) → RelatedPerson.
 * Responsável: selecionar, informar CPF ou cadastrar no próprio fluxo.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { currentFeatures, oids } from "@/configs/Settings";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
import { RelatedPersonVerificationList } from "@/components/schedule/RelatedPersonVerificationList";
import { mapPatientEntryToSearchRow } from "@/components/schedule/documentReissue/reissuePatientId";
import { useRelatedPersonVerification } from "@/hooks/useRelatedPersonVerification";
import { GetPatient, PostPatient } from "@/services/fhir";
import {
  fetchPatientRelatedPersons,
  getRelatedPersonIdentifierValue,
  getRelatedPersonResourceId,
} from "@/services/fhir/relatedperson/relatedPersonVerification";
import { SearchCPF } from "@/services/scim";
import { useFormatter } from "@/utils/useFormatter";

export interface ProntoSelectedPatient {
  reference: string;
  display: string;
  cpf: string;
  birthDate?: string;
  /** Telefone (telecom) quando disponível — WhatsApp/lembrete. */
  phone?: string;
  responsibleCpf?: string;
  responsibleName?: string;
}

interface ProntoAtendimentoPatientStepProps {
  onContinue: (patient: ProntoSelectedPatient) => void;
}

type ViewMode = "search" | "related" | "register" | "registerResponsible";

function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function isMinor(birthDate?: string) {
  if (!birthDate) return false;
  const age = dayjs().diff(dayjs(birthDate), "year");
  return age < 18;
}

type PersonFormState = {
  cpf: string;
  name: string;
  birth: string;
  gender: string;
  phone: string;
  serpro: any;
};

const emptyPersonForm = (): PersonFormState => ({
  cpf: "",
  name: "",
  birth: "",
  gender: "",
  phone: "",
  serpro: null,
});

export function ProntoAtendimentoPatientStep({
  onContinue,
}: ProntoAtendimentoPatientStepProps) {
  const { organization } = useOrganizationContext();
  const { patients, patientOrganizationLoading, refetchPatientOrganization } =
    usePatientOrganizationContext();

  const [search, setSearch] = useState("");
  const [view, setView] = useState<ViewMode>("search");
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState<ProntoSelectedPatient | null>(null);
  const [activeRelated, setActiveRelated] = useState<any[]>([]);
  const [inactiveRelated, setInactiveRelated] = useState<any[]>([]);
  const [chosenRelatedCpf, setChosenRelatedCpf] = useState("");
  const [informResponsible, setInformResponsible] = useState(false);
  const [manualResponsibleCpf, setManualResponsibleCpf] = useState("");

  const [patientForm, setPatientForm] = useState<PersonFormState>(emptyPersonForm);
  const [responsibleForm, setResponsibleForm] =
    useState<PersonFormState>(emptyPersonForm);

  const dismissedRelatedIdsRef = useRef<string[]>([]);
  /** Evita repetir GetPatient/Serpro para o mesmo CPF já consultado. */
  const lastAutoMpiCpfRef = useRef("");

  const rows = useMemo(
    () => (patients || []).map(mapPatientEntryToSearchRow),
    [patients],
  );

  const query = search.trim();
  const queryDigits = onlyDigits(query);

  const searchResults = useMemo(() => {
    if (!query) return [];
    const qLower = query.toLowerCase();
    return rows.filter((row) => {
      const idDigits = row.identifier?.replace(/[^a-zA-Z0-9]/g, "") || "";
      return (
        row.name.toLowerCase().includes(qLower) ||
        (queryDigits.length > 0 && idDigits.includes(queryDigits))
      );
    });
  }, [rows, query, queryDigits]);

  const hasSearched = query.length > 0;
  const notFoundInOrg = hasSearched && searchResults.length === 0;

  const loadRelated = useCallback(async (cpf: string) => {
    if (!currentFeatures.defaultApp) {
      setActiveRelated([]);
      setInactiveRelated([]);
      return;
    }
    try {
      const { activeRelated: active, inactiveRelated: inactive } =
        await fetchPatientRelatedPersons(cpf, dismissedRelatedIdsRef.current);
      setActiveRelated(active);
      setInactiveRelated(inactive);
    } catch {
      setActiveRelated([]);
      setInactiveRelated([]);
    }
  }, []);

  const {
    handleVerify,
    handleDeny,
    isRowLoading,
    isVerifyLoading,
    isDenyLoading,
  } = useRelatedPersonVerification({
    patientId: selected?.cpf || "",
    onAfterVerify: async () => {
      if (selected?.cpf) await loadRelated(selected.cpf);
    },
    onAfterDeny: async (_entry, resourceId) => {
      dismissedRelatedIdsRef.current = [
        ...dismissedRelatedIdsRef.current,
        resourceId,
      ];
      if (selected?.cpf) await loadRelated(selected.cpf);
    },
  });

  const buildPatientFromResource = (
    resource: fhir5.Patient,
  ): ProntoSelectedPatient => {
    const id = resource.id || "";
    const cpf =
      currentFeatures.defaultApp && id.includes("-")
        ? id.split("-").slice(1).join("-")
        : onlyDigits(resource.identifier?.[0]?.value || "");
    const name =
      resource.name?.find((n) => n.use === "official" || n.use === "usual")
        ?.text ||
      resource.name?.[0]?.text ||
      "";
    return {
      reference: `Patient/${id}`,
      display: useFormatter.formatNameDisplay(name),
      cpf: onlyDigits(cpf),
      birthDate: resource.birthDate,
      phone: resource.telecom?.find((t) => t.system === "phone")?.value,
    };
  };

  const proceedToRelatedOrContinue = async (patient: ProntoSelectedPatient) => {
    setSelected(patient);
    setChosenRelatedCpf("");
    setInformResponsible(false);
    setManualResponsibleCpf("");
    setLoading(true);
    try {
      await loadRelated(patient.cpf);
      setView("related");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFromList = async (
    row: ReturnType<typeof mapPatientEntryToSearchRow>,
  ) => {
    const patient: ProntoSelectedPatient = {
      reference: `Patient/${row.patientResourceId || `${oids.cpf}-${row.patientId}`}`,
      display: row.name,
      cpf: row.patientId,
    };
    const entry = patients?.find(
      (e) =>
        e.resource?.id === row.patientResourceId ||
        e.resource?.id?.endsWith(`-${row.patientId}`),
    );
    if (entry?.resource?.birthDate) {
      patient.birthDate = entry.resource.birthDate;
    }
    const phone = entry?.resource?.telecom?.find(
      (t) => t.system === "phone",
    )?.value;
    if (phone) patient.phone = phone;
    await proceedToRelatedOrContinue(patient);
  };

  /** Serpro pré-preenche formulário (paciente ou responsável). */
  const bootstrapSerpro = async (
    cpf: string,
    target: "patient" | "responsible",
  ) => {
    setLoading(true);
    try {
      const serproSearch = await SearchCPF(cpf);
      if (serproSearch?.status === 400) {
        toast.warn("CPF inválido. Verifique os dados.");
        return;
      }
      if (serproSearch?.status === 422 || serproSearch?.status === 451) {
        if (target === "patient") {
          toast.warn(
            "Paciente menor de idade — será necessário informar um responsável.",
          );
          setInformResponsible(true);
        } else {
          toast.warn(
            "O CPF informado pertence a um menor. Informe um responsável maior de idade.",
          );
          return;
        }
      }
      if (serproSearch?.data) {
        const patch: Partial<PersonFormState> = {
          serpro: serproSearch.data,
          name: serproSearch.data.nome || "",
        };
        if (serproSearch.data.nascimento) {
          const bd = dayjs(serproSearch.data.nascimento, "DDMMYYYY");
          if (bd.isValid()) patch.birth = bd.format("YYYY-MM-DD");
        }
        if (target === "patient") {
          setPatientForm((prev) => ({ ...prev, ...patch }));
        } else {
          setResponsibleForm((prev) => ({ ...prev, ...patch }));
        }
      }
    } catch {
      toast.info(
        "Não foi possível validar no Serpro. Preencha os dados manualmente.",
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * CPF completo e ausente na org → GetPatient (MPI) automático.
   * Se não achar → Serpro + formulário de cadastro.
   */
  const lookupCpfInMpiThenRegister = useCallback(
    async (cpf: string) => {
      if (cpf.length !== 11) return;

      setLoading(true);
      try {
        const response = await GetPatient({ identifier: cpf });
        if (response?.data) {
          toast.success("Paciente encontrado no MPI.");
          await proceedToRelatedOrContinue(
            buildPatientFromResource(response.data),
          );
          return;
        }
      } catch {
        // Não encontrado → cadastro via Serpro
      } finally {
        setLoading(false);
      }

      setPatientForm({
        ...emptyPersonForm(),
        cpf: useFormatter.formatCPF(cpf),
      });
      setView("register");
      toast.info("Paciente não encontrado. Complete o cadastro.");
      void bootstrapSerpro(cpf, "patient");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization?.id],
  );

  // Ao terminar de digitar o CPF (11 dígitos) e não achar na org → busca MPI
  useEffect(() => {
    if (view !== "search") return;
    if (patientOrganizationLoading) return;

    if (queryDigits.length !== 11) {
      lastAutoMpiCpfRef.current = "";
      return;
    }

    // Ainda há match na organização — não vai ao MPI
    if (searchResults.length > 0) return;

    if (lastAutoMpiCpfRef.current === queryDigits) return;
    lastAutoMpiCpfRef.current = queryDigits;

    void lookupCpfInMpiThenRegister(queryDigits);
  }, [
    view,
    queryDigits,
    searchResults.length,
    patientOrganizationLoading,
    lookupCpfInMpiThenRegister,
  ]);

  /**
   * Botão manual (ex.: usuário digitou nome e depois CPF incompleto).
   * Com CPF completo o useEffect já dispara sozinho.
   */
  const handleCadastrarPaciente = async () => {
    if (queryDigits.length !== 11) {
      toast.info(
        "Informe um CPF válido (11 dígitos) para cadastrar o paciente.",
      );
      return;
    }
    lastAutoMpiCpfRef.current = queryDigits;
    await lookupCpfInMpiThenRegister(queryDigits);
  };

  const postPerson = async (form: PersonFormState) => {
    const cpf = onlyDigits(form.cpf);
    if (cpf.length !== 11) {
      toast.info("Informe um CPF válido.");
      return null;
    }
    const name = (form.serpro?.nome || form.name).trim();
    if (!name) {
      toast.info("Informe o nome completo.");
      return null;
    }
    if (!form.gender) {
      toast.info("Informe o sexo.");
      return null;
    }
    if (!form.phone.replace(/\D/g, "")) {
      toast.info("Informe o telefone.");
      return null;
    }

    try {
      const existing = await GetPatient({ identifier: cpf });
      if (existing?.data) {
        return buildPatientFromResource(existing.data);
      }
    } catch {
      // cria
    }

    const body = {
      name,
      identifier: form.serpro?.ni || cpf,
      birthDate: dayjs(form.serpro?.nascimento, "DDMMYYYY").isValid()
        ? dayjs(form.serpro.nascimento, "DDMMYYYY").format("YYYY-MM-DD")
        : form.birth
          ? dayjs(form.birth).format("YYYY-MM-DD")
          : undefined,
      phoneNumber: form.phone.replace(/\D/g, ""),
      managingOrganization: organization?.id,
      gender: form.gender,
    };

    const mpiResponse = await PostPatient(body);
    if (!mpiResponse?.data) {
      toast.error("Erro ao cadastrar.");
      return null;
    }
    return buildPatientFromResource(mpiResponse.data);
  };

  const handleRegisterPatient = async () => {
    setLoading(true);
    try {
      const created = await postPerson(patientForm);
      if (!created) return;
      toast.success("Paciente cadastrado com sucesso.");
      refetchPatientOrganization();
      await proceedToRelatedOrContinue(created);
    } catch {
      toast.error("Não foi possível cadastrar o paciente.");
    } finally {
      setLoading(false);
    }
  };

  const openRegisterResponsible = (cpfFormatted: string) => {
    const digits = onlyDigits(cpfFormatted);
    setResponsibleForm({
      ...emptyPersonForm(),
      cpf: useFormatter.formatCPF(digits),
    });
    setView("registerResponsible");
    if (digits.length === 11) {
      void bootstrapSerpro(digits, "responsible");
    }
  };

  const handleRegisterResponsible = async () => {
    setLoading(true);
    try {
      const created = await postPerson(responsibleForm);
      if (!created) return;
      if (isMinor(created.birthDate)) {
        toast.warn("O responsável deve ser maior de idade.");
        return;
      }
      toast.success("Responsável cadastrado com sucesso.");
      refetchPatientOrganization();
      if (!selected) return;
      onContinue({
        ...selected,
        responsibleCpf: created.cpf,
        responsibleName: created.display,
      });
    } catch {
      toast.error("Não foi possível cadastrar o responsável.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinishRelated = async () => {
    if (!selected) return;

    let responsibleCpf = "";
    let responsibleName = "";

    if (chosenRelatedCpf) {
      const entry = activeRelated.find(
        (e) =>
          useFormatter.formatCPF(getRelatedPersonIdentifierValue(e) || "") ===
            chosenRelatedCpf ||
          onlyDigits(getRelatedPersonIdentifierValue(e) || "") ===
            onlyDigits(chosenRelatedCpf),
      );
      responsibleCpf = onlyDigits(
        getRelatedPersonIdentifierValue(entry) || chosenRelatedCpf,
      );
      responsibleName = entry?.resource?.name?.[0]?.text || "";
    } else if (
      informResponsible ||
      (isMinor(selected.birthDate) && activeRelated.length === 0)
    ) {
      responsibleCpf = onlyDigits(manualResponsibleCpf);
      if (responsibleCpf.length !== 11) {
        toast.info("Informe o CPF do responsável (11 dígitos).");
        return;
      }
      setLoading(true);
      try {
        const resp = await GetPatient({ identifier: responsibleCpf });
        if (resp?.data) {
          const built = buildPatientFromResource(resp.data);
          if (isMinor(built.birthDate)) {
            toast.warn("O responsável deve ser maior de idade.");
            return;
          }
          responsibleName = built.display;
        } else {
          // Não está no MPI → cadastrar no próprio fluxo
          toast.info("Responsável não encontrado. Complete o cadastro.");
          openRegisterResponsible(manualResponsibleCpf);
          return;
        }
      } catch {
        toast.info("Responsável não encontrado. Complete o cadastro.");
        openRegisterResponsible(manualResponsibleCpf);
        return;
      } finally {
        setLoading(false);
      }
    } else if (isMinor(selected.birthDate) && !chosenRelatedCpf) {
      toast.warn(
        "Paciente menor de idade: selecione ou informe um responsável.",
      );
      return;
    }

    if (activeRelated.length > 0 && !chosenRelatedCpf && !informResponsible) {
      toast.info("Selecione o responsável legal.");
      return;
    }

    onContinue({
      ...selected,
      responsibleCpf: responsibleCpf || undefined,
      responsibleName: responsibleName || undefined,
    });
  };

  const renderPersonForm = (
    form: PersonFormState,
    setForm: React.Dispatch<React.SetStateAction<PersonFormState>>,
    title: string,
    onBack: () => void,
    onSubmit: () => void,
  ) => (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <TextField
        variant="filled"
        size="small"
        fullWidth
        label="CPF"
        value={form.cpf}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            cpf: useFormatter.formatCPF(e.target.value),
          }))
        }
      />
      <TextField
        variant="filled"
        size="small"
        fullWidth
        label="Nome completo"
        value={form.name}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, name: e.target.value }))
        }
        disabled={!!form.serpro?.nome}
      />
      <TextField
        variant="filled"
        size="small"
        fullWidth
        label="Data de nascimento"
        type="date"
        value={form.birth}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, birth: e.target.value }))
        }
        InputLabelProps={{ shrink: true }}
        disabled={
          !!form.serpro?.nascimento &&
          dayjs(form.serpro.nascimento, "DDMMYYYY").isValid()
        }
      />
      <TextField
        select
        variant="filled"
        size="small"
        fullWidth
        label="Sexo"
        value={form.gender}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, gender: e.target.value }))
        }
      >
        <MenuItem value="male">Masculino</MenuItem>
        <MenuItem value="female">Feminino</MenuItem>
      </TextField>
      <TextField
        variant="filled"
        size="small"
        fullWidth
        label="Telefone"
        value={form.phone}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, phone: e.target.value }))
        }
      />
      <Stack direction="row" spacing={1}>
        <Button
          onClick={onBack}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={() => void onSubmit()}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          {loading ? "Salvando..." : "Cadastrar e continuar"}
        </Button>
      </Stack>
    </Stack>
  );

  if (view === "register") {
    return renderPersonForm(
      patientForm,
      setPatientForm,
      "Cadastrar paciente",
      () => {
        setView("search");
        setPatientForm(emptyPersonForm());
        // Permite nova busca MPI se voltar e digitar o mesmo CPF
        lastAutoMpiCpfRef.current = "";
      },
      handleRegisterPatient,
    );
  }

  if (view === "registerResponsible") {
    return renderPersonForm(
      responsibleForm,
      setResponsibleForm,
      "Cadastrar responsável",
      () => {
        setView("related");
        setResponsibleForm(emptyPersonForm());
      },
      handleRegisterResponsible,
    );
  }

  if (view === "related" && selected) {
    return (
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Box>
          <Typography variant="body1" fontWeight={600}>
            {selected.display}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            CPF: {useFormatter.formatCPF(selected.cpf)}
          </Typography>
        </Box>

        {loading ? (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            {currentFeatures.defaultApp && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Responsável legal</Typography>
                <RelatedPersonVerificationList
                  entries={inactiveRelated}
                  isRowLoading={isRowLoading}
                  isVerifyLoading={isVerifyLoading}
                  isDenyLoading={isDenyLoading}
                  onVerify={handleVerify}
                  onDeny={handleDeny}
                  title="Pendentes de verificação"
                />

                {activeRelated.length > 0 && (
                  <TextField
                    select
                    variant="filled"
                    size="small"
                    fullWidth
                    label="Selecionar responsável"
                    value={chosenRelatedCpf}
                    onChange={(e) => {
                      setChosenRelatedCpf(e.target.value);
                      setInformResponsible(false);
                    }}
                  >
                    {activeRelated.map((entry) => {
                      const id = getRelatedPersonResourceId(entry) || "";
                      const cpfVal =
                        getRelatedPersonIdentifierValue(entry) || "";
                      return (
                        <MenuItem
                          key={id}
                          value={useFormatter.formatCPF(cpfVal)}
                        >
                          {entry.resource?.name?.[0]?.text} —{" "}
                          {useFormatter.formatCPF(cpfVal)}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                )}

                {(activeRelated.length === 0 || informResponsible) && (
                  <>
                    {activeRelated.length > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={informResponsible}
                            onChange={(e) => {
                              setInformResponsible(e.target.checked);
                              if (e.target.checked) setChosenRelatedCpf("");
                            }}
                          />
                        }
                        label="Informar outro responsável"
                      />
                    )}
                    {activeRelated.length === 0 &&
                      !isMinor(selected.birthDate) && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={informResponsible}
                              onChange={(e) =>
                                setInformResponsible(e.target.checked)
                              }
                            />
                          }
                          label="Informar responsável legal"
                        />
                      )}
                    {(informResponsible ||
                      (isMinor(selected.birthDate) &&
                        activeRelated.length === 0)) && (
                      <TextField
                        variant="filled"
                        size="small"
                        fullWidth
                        label="CPF do responsável"
                        value={manualResponsibleCpf}
                        onChange={(e) =>
                          setManualResponsibleCpf(
                            useFormatter.formatCPF(e.target.value),
                          )
                        }
                        helperText="Se não existir no MPI, você poderá cadastrá-lo em seguida."
                      />
                    )}
                  </>
                )}
              </Stack>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => {
                  setView("search");
                  setSelected(null);
                }}
                sx={{ textTransform: "none" }}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleFinishRelated()}
                sx={{ textTransform: "none" }}
              >
                Continuar
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Busque por nome ou CPF. Com CPF completo (11 dígitos), se não estiver na
        organização a busca no MPI acontece automaticamente.
      </Typography>

      <TextField
        variant="filled"
        label="Buscar por nome ou CPF"
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Ex.: Maria ou 000.000.000-00"
        autoFocus
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {patientOrganizationLoading && (
        <Stack alignItems="center" py={2}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {!patientOrganizationLoading && !hasSearched && (
        <Typography variant="body2" color="text.secondary">
          Digite o nome ou CPF para buscar.
        </Typography>
      )}

      {!patientOrganizationLoading && searchResults.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ borderRadius: 2, maxHeight: 240, overflow: "auto" }}
        >
          <List dense disablePadding>
            {searchResults.slice(0, 50).map((row) => (
              <ListItemButton
                key={row.patientResourceId || row.patientId}
                onClick={() => void handleSelectFromList(row)}
                disabled={loading}
              >
                <ListItemText
                  primary={row.name}
                  secondary={row.identifier || undefined}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      {notFoundInOrg && (
        <Stack spacing={1.5}>
          {loading && queryDigits.length === 11 ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Buscando paciente
              </Typography>
            </Stack>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Nenhum paciente na organização com “{query}”.
              </Typography>
              {queryDigits.length !== 11 && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Informe o CPF completo (11 dígitos) para buscar no MPI e
                    cadastrar automaticamente.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => void handleCadastrarPaciente()}
                    disabled={loading}
                    sx={{ textTransform: "none", alignSelf: "flex-start" }}
                  >
                    Cadastrar paciente
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
}
